import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, setToken as setAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GironeBadge from "@/components/GironeBadge";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, Eye, EyeOff } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Costruisce l'URL public del file (Emergent Object Storage passa via /api/public/files/:encoded). */
function assetUrl(path) {
  if (!path) return null;
  const enc = encodeURIComponent(path).replace(/%2F/g, "/");
  return `${BACKEND}/api/public/files/${enc}`;
}

export default function OnboardingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const [team, setTeam] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    password: "",
    password_confirm: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/onboarding/${token}`);
        if (mounted) setTeam(data);
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.detail || "Invito non valido");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const passwordsMatch = useMemo(
    () => form.password && form.password === form.password_confirm,
    [form.password, form.password_confirm],
  );
  const passwordStrong = form.password.length >= 8;
  const canSubmit = form.full_name.trim() && form.phone.trim() && form.email.trim() &&
                    passwordStrong && passwordsMatch;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      toast.error("Compila tutti i campi correttamente");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/onboarding/${token}/complete`, form);
      toast.success("Benvenuto! Account creato con successo");
      if (data?.token) setAuthToken(data.token);
      if (refresh) await refresh();
      // Redirect dopo un attimo per lasciare vedere il toast
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="onboarding-loading">
          Caricamento invito…
        </div>
      </div>
    );
  }

  if (err || !team) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16" data-testid="onboarding-error">
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-8 text-center">
            <ShieldAlert className="w-12 h-12 text-red-600 mx-auto mb-3" />
            <h1 className="display font-black text-2xl text-red-900">Invito non disponibile</h1>
            <p className="text-red-900/80 mt-2">{err || "Il link non è valido o è già stato usato."}</p>
            <Button className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => navigate("/login")}>Vai al login</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12" data-testid="onboarding-page">
        <div className="text-xs uppercase tracking-widest text-slate-500 fade-up">Benvenuto al Fantacampionato</div>
        <h1 className="display font-black text-4xl md:text-5xl mt-1 fade-up">
          Sei il nuovo <span style={{ color: "var(--brand)" }}>fantallenatore</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-8">
          {/* Team preview */}
          <div className="md:col-span-2 card-surface rounded-2xl p-5 space-y-4" data-testid="onboarding-team-preview">
            <div className="flex items-center gap-3">
              <GironeBadge girone={team.girone} size={40} />
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Girone {team.girone}</div>
                <div className="display font-black text-xl">{team.team_name}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <AssetTile label="Stemma" src={assetUrl(team.shield_path)} testid="onb-shield" />
              <AssetTile label="Kit" src={assetUrl(team.jersey_path)} testid="onb-jersey" />
              <AssetTile label="Mister" src={assetUrl(team.coach_path)} testid="onb-coach" />
            </div>
            <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
              Rosa acquistata all'asta: <b className="text-slate-700">{team.players_count} calciatori</b>. Potrai aggiornare stemma, kit e foto del mister dopo aver completato la registrazione.
            </div>
          </div>

          {/* Form registrazione */}
          <form onSubmit={submit} className="md:col-span-3 card-surface rounded-2xl p-5 md:p-6 space-y-4"
                data-testid="onboarding-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Completa il tuo profilo</div>
              <h2 className="display font-bold text-xl mt-0.5">I tuoi dati</h2>
              <p className="text-xs text-slate-500 mt-1">Verrai approvato automaticamente e avrai subito accesso al Fantacampionato.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 text-xs">Nome e Cognome</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                       placeholder="Mario Rossi" required data-testid="onb-fullname" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Numero di telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       placeholder="+39 333 1234567" required data-testid="onb-phone" className="mt-1 bg-white" />
              </div>
            </div>

            <div>
              <Label className="text-slate-600 text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="tuo.nome@email.com" required data-testid="onb-email" className="mt-1 bg-white" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 text-xs">Password (min 8 caratteri)</Label>
                <div className="relative mt-1">
                  <Input type={showPw ? "text" : "password"} value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         required data-testid="onb-password" className="bg-white pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          aria-label="Mostra password">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form.password && !passwordStrong && (
                  <div className="text-[10px] text-red-600 mt-1">Almeno 8 caratteri</div>
                )}
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Conferma password</Label>
                <Input type={showPw ? "text" : "password"} value={form.password_confirm}
                       onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                       required data-testid="onb-password-confirm" className="mt-1 bg-white" />
                {form.password_confirm && !passwordsMatch && (
                  <div className="text-[10px] text-red-600 mt-1">Non coincide</div>
                )}
                {passwordsMatch && (
                  <div className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Coincidono
                  </div>
                )}
              </div>
            </div>

            <Button type="submit" disabled={!canSubmit || submitting}
                    className="btn-brand rounded-full w-full h-11"
                    data-testid="onb-submit">
              {submitting ? "Creazione account…" : `Diventa allenatore di ${team.team_name}`}
            </Button>
            <div className="text-center text-[10px] text-slate-400">
              Cliccando prosegui accetti i termini e la privacy policy.
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function AssetTile({ label, src, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white aspect-square flex flex-col items-center justify-center p-2"
         data-testid={testid}>
      {src ? (
        <img src={src} alt={label} className="w-full h-full object-contain" />
      ) : (
        <div className="text-slate-300 text-[10px] uppercase tracking-widest text-center">
          {label}<br />non disponibile
        </div>
      )}
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
    </div>
  );
}
